import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { Mail, Check, UserPlus, Clock, XCircle, ShieldCheck, ArrowLeft, Trash2 } from 'lucide-react';
import styles from './MiEquipoPremium.module.css';

interface MiembroEquipo {
  miembro_uuid: string;
  perfiles_usuario: {
    nombres_completos: string | null;
    apellidos_completos: string | null;
    perfil_correo: string;
    avatar_url?: string | null;
  } | null;
  rol_workspace: string;
  estado: string;
}

const getInitials = (name: string) => {
  if (!name) return 'EQ';
  const words = name.trim().split(/\s+/).filter(w => w.length > 0);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return 'EQ';
};

export function MiEquipoScreen() {
  const navigate = useNavigate();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  
  // Tab state for Ergonomic layout
  const [activeTab, setActiveTab] = useState<'secretaria' | 'equipos'>('secretaria');

  const [miembros, setMiembros] = useState<MiembroEquipo[]>([]);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Escenario 2: Equipos Clínicos
  const [equipos, setEquipos] = useState<any[]>([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);

  useEffect(() => {
    initWorkspace();
  }, []);

  const initWorkspace = async () => {
    setLoading(true);
    try {
      const { data: negocioUuid, error: rpcError } = await supabase.rpc('fn_crear_workspace_personal_clinico');
      
      if (rpcError) throw rpcError;
      setWorkspaceId(negocioUuid);

      await Promise.all([
        loadMiembros(negocioUuid),
        loadEquipos()
      ]);
    } catch (error: any) {
      console.error('Error inicializando equipo:', error);
      setMessage({ type: 'error', text: 'No se pudo cargar la información del equipo.' });
    } finally {
      setLoading(false);
    }
  };

  const loadEquipos = async () => {
    // Gracias al RLS, esto solo devuelve los equipos a los que pertenece el usuario
    const { data, error } = await supabase
      .from('equipos_clinicos')
      .select(`
        equipo_uuid,
        nombre_display,
        creado_por_uuid,
        equipo_miembros ( rol_equipo )
      `)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setEquipos(data);
    }
  };

  const loadMiembros = async (negocioId: string) => {
    const { data, error } = await supabase
      .from('workspace_miembros')
      .select(`
        miembro_uuid,
        rol_workspace,
        estado,
        perfiles_usuario!workspace_miembros_miembro_uuid_fkey (
          nombres_completos,
          apellidos_completos,
          perfil_correo,
          avatar_url
        )
      `)
      .eq('negocio_uuid', negocioId);

    if (error) {
      console.error('Error cargando miembros:', error);
      return;
    }
    
    setMiembros(data as any);
  };

  const handleInvite = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!emailInput.trim() || !workspaceId) return;

    setInviting(true);
    setMessage(null);

    try {
      const { data, error } = await supabase.rpc('fn_invitar_secretaria_por_email', {
        p_email: emailInput.trim(),
        p_negocio_uuid: workspaceId
      });

      if (error) throw error;
      if (data?.success === false) {
        throw new Error(data.error);
      }

      setMessage({ type: 'success', text: `Invitación enviada a ${emailInput}` });
      setEmailInput('');
      await loadMiembros(workspaceId);
    } catch (error: any) {
      console.error('Error invitando:', error);
      setMessage({ type: 'error', text: error.message || 'Error al enviar invitación.' });
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (miembroUuid: string) => {
    if (!workspaceId) return;
    if (!window.confirm('¿Seguro que deseas revocar el acceso a este usuario?')) return;
    
    try {
      const { error } = await supabase
        .from('workspace_miembros')
        .delete()
        .eq('negocio_uuid', workspaceId)
        .eq('miembro_uuid', miembroUuid);

      if (error) throw error;
      
      setMessage({ type: 'success', text: 'Acceso revocado correctamente.' });
      await loadMiembros(workspaceId);
    } catch (error: any) {
      console.error('Error eliminando miembro:', error);
      setMessage({ type: 'error', text: 'Error al revocar acceso.' });
    }
  };

  const handleCreateTeam = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;

    setCreatingTeam(true);
    setMessage(null);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("No autenticado");

      // 1. Crear el equipo (RLS permite insert si creado_por = auth.uid())
      const { data: teamData, error: teamError } = await supabase
        .from('equipos_clinicos')
        .insert({
          nombre_display: newTeamName.trim(),
          creado_por_uuid: userData.user.id
        })
        .select()
        .single();

      if (teamError) throw teamError;

      // 2. Insertarse a sí mismo como clinico_principal
      const { error: memberError } = await supabase
        .from('equipo_miembros')
        .insert({
          equipo_uuid: teamData.equipo_uuid,
          miembro_uuid: userData.user.id,
          rol_equipo: 'clinico_principal'
        });

      if (memberError) throw memberError;

      setMessage({ type: 'success', text: `Equipo "${newTeamName}" creado con éxito.` });
      setNewTeamName('');
      await loadEquipos();
    } catch (error: any) {
      console.error('Error creando equipo:', error);
      setMessage({ type: 'error', text: 'Error al crear el equipo interdisciplinario.' });
    } finally {
      setCreatingTeam(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100 text-slate-500">
        <div className={styles.spinnerDark} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <button
          className={styles.backBtn}
          onClick={() => { if (globalThis.history.length > 2) navigate(-1); else navigate('/pro'); }}
          aria-label="Volver"
        >
          <ArrowLeft size={24} color="#1E293B" />
        </button>
        <h1 className={styles.pageTitle}>Mi Equipo</h1>
        <p className={styles.pageSubtitle}>
          Delega la gestión de tu agenda a tu secretaria de forma segura.
        </p>
      </div>

      {message && (
        <div className={message.type === 'success' ? styles.messageSuccess : styles.messageError}>
          {message.type === 'success' ? <Check size={20} /> : <XCircle size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Tabs Ergonómicos */}
      <div className={styles.tabsContainer} style={{ display: 'flex', gap: '8px', marginBottom: '24px', background: 'rgba(255,255,255,0.5)', padding: '6px', borderRadius: '20px', backdropFilter: 'blur(10px)' }}>
        <button 
          onClick={() => setActiveTab('secretaria')}
          style={{ flex: 1, padding: '14px', borderRadius: '16px', fontWeight: 700, transition: 'all 0.2s', border: 'none', cursor: 'pointer', background: activeTab === 'secretaria' ? '#FFFFFF' : 'transparent', color: activeTab === 'secretaria' ? '#0F172A' : '#64748B', boxShadow: activeTab === 'secretaria' ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none' }}
        >
          Secretaría Personal
        </button>
        <button 
          onClick={() => setActiveTab('equipos')}
          style={{ flex: 1, padding: '14px', borderRadius: '16px', fontWeight: 700, transition: 'all 0.2s', border: 'none', cursor: 'pointer', background: activeTab === 'equipos' ? '#FFFFFF' : 'transparent', color: activeTab === 'equipos' ? '#0F172A' : '#64748B', boxShadow: activeTab === 'equipos' ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none' }}
        >
          Equipos Clínicos
        </button>
      </div>

      {activeTab === 'secretaria' && (
        <>
          {/* Tarjeta de Invitación */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.iconBox}>
                <UserPlus size={24} color="#059669" />
              </div>
              <h2 className={styles.cardTitle}>Invitar Secretaria</h2>
            </div>
            
            <form onSubmit={handleInvite} className={styles.formWrapper}>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <Mail size={20} color="#94A3B8" />
                </div>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  required
                  className={styles.input}
                />
              </div>
              <button
                type="submit"
                disabled={inviting || !emailInput.trim()}
                className={`${styles.button} ${inviting || !emailInput.trim() ? styles.buttonDisabled : ''}`}
              >
                {inviting ? (
                  <>
                    <div className={styles.spinnerWhite} />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <span>Invitar al Equipo</span>
                )}
              </button>
            </form>
            <div className={styles.hint}>
              <ShieldCheck size={16} color="#059669" className="shrink-0 mt-0.5" />
              <span>El usuario debe haber creado una cuenta en Aura previamente. Su acceso será restringido únicamente a labores logísticas.</span>
            </div>
          </div>

          {/* Lista de Miembros */}
          <div className={styles.cardList}>
            <div className={styles.cardListHeader}>
              <h2 className={styles.cardTitle}>Miembros del Consultorio</h2>
            </div>
            <div className={styles.memberList}>
              {miembros.length === 0 ? (
                <div className={styles.emptyState}>
                  Aún no has invitado a nadie a tu equipo.
                </div>
              ) : (
                miembros.map((miembro) => (
                  <div key={miembro.miembro_uuid} className={styles.memberItem}>
                    <div className={styles.memberInfo}>
                      <div className={styles.avatar}>
                        {miembro.perfiles_usuario?.avatar_url ? (
                          <img src={miembro.perfiles_usuario.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                          miembro.perfiles_usuario?.nombres_completos?.[0] || miembro.perfiles_usuario?.perfil_correo?.[0]?.toUpperCase() || 'U'
                        )}
                      </div>
                      <div>
                        <h3 className={styles.memberName}>
                          {miembro.perfiles_usuario?.nombres_completos || 'Usuario Invitado'} {miembro.perfiles_usuario?.apellidos_completos || ''}
                          {miembro.rol_workspace === 'administrador' && (
                            <span className={styles.badgeAdmin}>Tú (Admin)</span>
                          )}
                        </h3>
                        <p className={styles.memberEmail}>{miembro.perfiles_usuario?.perfil_correo || 'Invitación pendiente'}</p>
                      </div>
                    </div>
                    
                    <div className={styles.memberActions}>
                      {miembro.estado === 'pendiente' ? (
                        <div className={styles.badgePending}>
                          <Clock size={16} />
                          <span>Pendiente</span>
                        </div>
                      ) : (
                        <div className={styles.badgeActive}>
                          {miembro.rol_workspace}
                        </div>
                      )}
                      {miembro.rol_workspace !== 'administrador' && (
                        <button 
                          className={styles.removeBtn}
                          onClick={() => handleRemoveMember(miembro.miembro_uuid)}
                          title="Revocar acceso"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* SECCIÓN B: ESCENARIO 2 - EQUIPOS CLÍNICOS */}
      {activeTab === 'equipos' && (
        <div className={`${styles.cardList} ${styles.teamCardList}`}>
          <div className={`${styles.cardListHeader} ${styles.teamCardHeader}`}>
            <h2 className={`${styles.cardTitle} ${styles.teamCardTitle}`}>Equipos Interdisciplinarios</h2>
            <p className={`${styles.pageSubtitle} ${styles.teamSubtitle}`}>
              Comparte una agenda centralizada con otros médicos para coordinar cirugías y turnos.
            </p>
          </div>

          {/* Crear Equipo */}
          <div className={styles.teamFormContainer}>
            <form onSubmit={handleCreateTeam} className={styles.formWrapper}>
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Nombre del Equipo (ej. Equipo Quirúrgico Alfa)"
                  required
                  className={`${styles.input} ${styles.teamInput}`}
                />
              </div>
              <button
                type="submit"
                disabled={creatingTeam || !newTeamName.trim()}
                className={`${styles.button} ${creatingTeam || !newTeamName.trim() ? styles.buttonDisabled : styles.teamButton}`}
              >
                {creatingTeam ? 'Creando...' : 'Crear Equipo'}
              </button>
            </form>
          </div>

          {/* Lista de Equipos */}
          <div className={styles.memberList}>
            {equipos.length === 0 ? (
              <div className={styles.emptyState}>
                No perteneces a ningún equipo clínico interdisciplinario.
              </div>
            ) : (
              equipos.map((equipo) => (
                <div key={equipo.equipo_uuid} className={styles.memberItem}>
                  <div className={styles.memberInfo}>
                    <div className={`${styles.avatar} ${styles.teamAvatar}`} style={{ fontSize: '16px', fontWeight: 'bold' }}>
                      {getInitials(equipo.nombre_display)}
                    </div>
                    <div>
                      <h3 className={styles.memberName}>
                        {equipo.nombre_display}
                      </h3>
                      <p className={styles.memberEmail}>
                        {equipo.equipo_miembros?.length || 0} miembros
                      </p>
                    </div>
                  </div>
                  
                  <div className={styles.memberActions}>
                    <button 
                      className={`${styles.button} ${styles.teamManageBtn}`} 
                      onClick={() => navigate(`/pro/equipo/${equipo.equipo_uuid}`)}
                    >
                      Gestionar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
